"use strict";

var __importDefault = void 0 && (void 0).__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

var mobx_state_tree_1 = require("mobx-state-tree");

var underscore_1 = require("underscore");

var RuleModel_1 = __importDefault(require("./RuleModel"));

var ColorPickerModel_1 = __importDefault(require("./ColorPickerModel"));

var ConditionalFormatSpecHelper_1 = require("../../definitions/properties/util/ConditionalFormatSpecHelper");

var PropertyModelSpec_1 = require("../../definitions/properties/PropertyModelSpec");

var RuleConstants_1 = require("../../definitions/constants/RuleConstants");

var ColorTypes_1 = require("./ColorTypes");

var PARENT_LEVEL = 2; // Go up twice, up once to reach to ConditionalFormatsModel.conditionalFormats then up one more level to reach the ConditionalFormatsModel model

var GRAND_PARENT_LEVEL = 3;
var KPI_MAX_RULE_LIMIT = 3;
var PaletteModel = mobx_state_tree_1.types.model('PaletteModel', {
  label: mobx_state_tree_1.types.optional(mobx_state_tree_1.types.string, ''),
  fills: mobx_state_tree_1.types.optional(mobx_state_tree_1.types.array(ColorTypes_1.FillModel), [])
}).views(function (self) {
  return {
    getLabel: function getLabel() {
      return self.label;
    },
    getFills: function getFills() {
      return self.fills;
    }
  };
}).actions(function (self) {
  return {
    setLabel: function setLabel(value) {
      self.label = value;
    },
    setFills: function setFills(fills) {
      var _a;

      self.fills.length = 0;

      (_a = self.fills).push.apply(_a, fills);
    }
  };
});
var ConditionalFormatModelFactory = mobx_state_tree_1.types.model('ConditionalFormatModel', {
  dataItemId: mobx_state_tree_1.types.string,
  paletteId: mobx_state_tree_1.types.string,
  colorBy: mobx_state_tree_1.types.maybe(mobx_state_tree_1.types.string),
  backgroundOpacity: mobx_state_tree_1.types.maybe(mobx_state_tree_1.types.number),
  scale: mobx_state_tree_1.types.string,
  rules: mobx_state_tree_1.types.optional(mobx_state_tree_1.types.array(RuleModel_1.default), []),
  palette: mobx_state_tree_1.types.optional(PaletteModel, {
    label: '',
    fills: []
  }),
  colorPickerModel: mobx_state_tree_1.types.maybe(ColorPickerModel_1.default),
  displayRuleNotification: mobx_state_tree_1.types.optional(mobx_state_tree_1.types.boolean, true),
  hasMinMax: mobx_state_tree_1.types.optional(mobx_state_tree_1.types.boolean, false)
}).volatile(function () {
  return {
    parent: null
  };
}).actions(function (self) {
  return {
    _setPaletteValues: function _setPaletteValues(paletteSpec) {
      if (paletteSpec === void 0) {
        paletteSpec = {};
      }

      self.palette.setLabel(paletteSpec.label);
      self.palette.setFills(paletteSpec.fills);
    }
  };
}).actions(function (self) {
  return {
    afterCreate: function afterCreate() {
      self.parent = mobx_state_tree_1.getParent(self, PARENT_LEVEL);
      var paletteId = self.paletteId;
      var ancestorLevel = GRAND_PARENT_LEVEL;
      self.colorPickerModel = ColorPickerModel_1.default.create({
        paletteId: paletteId,
        ancestorLevel: ancestorLevel
      });
      self.rules.forEach(function (ruleModel) {
        ruleModel.setConditionTypeOptions(self.scale);
      });
    },

    /**
     * adds a new rule
     * @param rule new rule to add
     */
    addRule: function addRule() {
      self.parent.getConditionalFormattingApi().addRule(self.dataItemId, self.rules.length, RuleConstants_1.RULE_TYPES.THRESHOLD); // The new rule is always being added to the dend of the collectionn of rules

      var newRuleIndex = self.rules.length - 1; // Expand the newly added rule

      self.rules[newRuleIndex].setExpand('ConditionalFormatModel:addRule', newRuleIndex, true);
    },

    /**
     * Removes a rule
     * @param {number} index position of the rule to remove
     */
    deleteRule: function deleteRule(index) {
      self.parent.getConditionalFormattingApi().deleteRule(self.dataItemId, index);
    },

    /**
     * Moves a rule
     * @param {number} fromIndex from index of the rule to move
     * @param {number} toIndex to index of the rule to move
     */
    moveRule: function moveRule(fromIndex, toIndex) {
      self.parent.getConditionalFormattingApi().moveRule(self.dataItemId, fromIndex, toIndex);
    },
    setPaletteId: function setPaletteId(paletteId) {
      if (self.paletteId !== paletteId) {
        self.parent.getConditionalFormattingApi().setPaletteId(self.dataItemId, paletteId);
      }
    },
    setScale: function setScale(scale) {
      if (self.scale !== scale) {
        self.parent.getConditionalFormattingApi().setScale(self.dataItemId, scale);
      }
    },

    /**
     * Sets the backgroundOpacity value
     *
     * @param {number} backgroundOpacity set the background opacity
     */
    setBackgroundOpacity: function setBackgroundOpacity(backgroundOpacity) {
      if (self.backgroundOpacity !== backgroundOpacity) {
        self.parent.getConditionalFormattingApi().setBackgroundOpacity(self.dataItemId, backgroundOpacity);
      }
    },
    loadPalette: function loadPalette() {
      return self.parent.loadPalette(self.paletteId).then(function (palette) {
        self._setPaletteValues(palette);
      });
    },
    setDisplayRuleNotification: function setDisplayRuleNotification(value) {
      self.displayRuleNotification = value;
    }
  };
}).actions(function (self) {
  return {
    /**
     * Sets the backgroundOpacity value
     * Use this API to update the scale value retrieve from the content model
     *
     * @param {number} backgroundOpacity set the background opacity
     */
    setBackgroundOpacityValueFromContentModel: function setBackgroundOpacityValueFromContentModel(backgroundOpacity) {
      self.backgroundOpacity = backgroundOpacity;
    },

    /**
     * Sets the palette id
     * Use this API to update the scale value retrieve from the content model
     *
     * @param {string} paletteId the paletteId
     */
    setPaletteIdValueFromContentModel: function setPaletteIdValueFromContentModel(paletteId) {
      self.paletteId = paletteId;
      self.colorPickerModel.setPaletteId(paletteId); // Load the palette for the paletteId

      self.loadPalette();
    },

    /**
     * Sets the scale value
     * Use this API to update the scale value retrieve from the content model
     *
     * @param {string} scale the scale value
     */
    setScaleValueFromContentModel: function setScaleValueFromContentModel(scale) {
      self.scale = scale;
      self.rules.forEach(function (ruleModel) {
        ruleModel.setConditionTypeOptions(scale);
      });
    },

    /**
     * Sets the scale value
     * Use this API to update the scale value retrieve from the content model
     *
     * @param {string} value the value to set
     */
    setColorByValueFromContentModel: function setColorByValueFromContentModel(value) {
      self.colorBy = value;
    },

    /**
     * Adds a rule
     */
    addRuleValueFromContentModel: function addRuleValueFromContentModel(index, ruleToAdd) {
      if (!(ruleToAdd instanceof PropertyModelSpec_1.Rule)) {
        throw new Error('Invalid ruleToAdd to mst model');
      }

      var spec = ruleToAdd.getMSTJsonSpec();
      self.rules.splice(index, 0, spec);
    },

    /**
     * Delete a rule
     * @param {number} index position of the rule to remove
     */
    deleteRuleValueFromContentModel: function deleteRuleValueFromContentModel(index) {
      self.setDisplayRuleNotification(true);
      mobx_state_tree_1.destroy(self.rules[index]);
    },

    /**
     * Move a rule
     * @param {number} fromIndex from index of the rule to move
     * @param {number} toIndex to index of the rule to move
     */
    moveRuleValueFromContentModel: function moveRuleValueFromContentModel(fromIndex, toIndex) {
      self.rules.splice(toIndex, 0, mobx_state_tree_1.detach(self.rules[fromIndex]));
    },

    /**
     * Set the current colorBy
     */
    setCurrentColorBy: function setCurrentColorBy(colorBy) {
      if (self.colorBy !== colorBy) {
        self.parent.getConditionalFormattingApi().setColorBy(self.dataItemId, colorBy);
      }
    },

    /**
     * Sets flag indicating if min/max is available
     * The min/max is not stored in the content model but is defined conditional format model
     *
     * @param {string} value the value to set
     */
    setHasMinMaxValueFromContentModel: function setHasMinMaxValueFromContentModel(value) {
      self.hasMinMax = value;
    }
  };
}).views(function (self) {
  return {
    getDataItemId: function getDataItemId() {
      return self.dataItemId;
    },
    getPalette: function getPalette() {
      return self.palette;
    },
    getPaletteId: function getPaletteId() {
      return self.paletteId;
    },
    getColorBy: function getColorBy() {
      return self.colorBy;
    },
    getBackgroundOpacity: function getBackgroundOpacity() {
      return self.backgroundOpacity;
    },
    getScale: function getScale() {
      return self.scale;
    },
    getRules: function getRules() {
      return self.rules;
    },
    getRuleByIndex: function getRuleByIndex(idx) {
      return idx < self.rules.length ? self.rules[idx] : null;
    },
    getColorPickerModel: function getColorPickerModel() {
      return self.colorPickerModel;
    },
    getDisplayRuleNotification: function getDisplayRuleNotification() {
      return self.displayRuleNotification;
    },
    getHasMinMax: function getHasMinMax() {
      return self.hasMinMax;
    },

    get shouldAddRuleBeDisabled() {
      var disableAddRule = ConditionalFormatSpecHelper_1.ConditionalFormatSpecHelper.isKpiVis(self.parent.getVisType()) && self.rules.length === RuleConstants_1.RULE_KPI_LIMIT;

      if (!disableAddRule && !self.rules.length && self.scale === RuleConstants_1.RULE_SCALES.NUMERIC) {
        disableAddRule = !self.hasMinMax;
      }

      return disableAddRule;
    },

    getFormattedValue: function getFormattedValue(value) {
      var isPercent = self.scale === RuleConstants_1.RULE_SCALES.PERCENTAGE;
      var formattedValue;

      if (underscore_1.isNumber(value)) {
        formattedValue = self.parent.getUIContext().format(value, {
          maximumFractionDigits: RuleConstants_1.RULE_CONDITION_MAXIMUM_FRACTION_DIGITS,
          type: 'number'
        }) + (isPercent ? RuleConstants_1.RULE_SCALES.PERCENTAGE : '');
      }

      return formattedValue;
    },

    /**
     * @return {string} the data column id that is use to represent the colorBy value
     */
    get currentColorBy() {
      // A React Component using this model should access the model's properties through the views functions to display
      // the value in the UI
      return self.colorBy;
    },

    get hasColorBy() {
      return self.colorBy && self.colorBy.length > 0;
    },

    get numberOfRules() {
      return self.rules.length;
    },

    get hasReachMaxRuleLimit() {
      return ConditionalFormatSpecHelper_1.ConditionalFormatSpecHelper.isKpiVis(self.parent.getVisType()) && self.rules.length >= KPI_MAX_RULE_LIMIT;
    }

  };
});
exports.default = ConditionalFormatModelFactory;
